import React from 'react';
import { i18n } from 'config/locales';

import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import { ReprimandModel } from 'forms/sections/history/employment/types';
import {
  CommonOnUpdate, CommonOnError, CommonRequired, CommonScrollIntoView
} from 'types/props';
import { AccordionItemProps } from 'components/Form/Accordion/types';
import { setAttribute } from 'components/Form/shared/setAttribute';
import Field from 'components/Form/Field';
import Textarea from 'components/Form/Textarea';
import Text from 'components/Form/Text';
import DateControl from 'components/Form/DateControl';
import { getSafeDateTime } from 'helpers/date';
import ErrorMessage from 'components/ErrorMessage';
import { disciplineOptions, disciplineSentenceOptions, militaryDisciplinaryProcedureNamesPvq } from 'constants/enums/militaryOptions';
import RadioGroup from '../RadioGroup';
import Radio from '../Radio';
import PVQName from '../PVQName';
import NotApplicable from '../NotApplicable';
import TelephoneCollection from '../Telephone/TelephoneCollection';
import EmailCollection from '../PVQEmail/EmailCollection';
import CheckboxGroup from '../CheckboxGroup';
import Checkbox from '../Checkbox';

interface Props
  extends ValidationElementProps,
  Partial<ReprimandModel>,
  Omit<AccordionItemProps, 'name'> {
  hideGivenByEmail?: boolean
  hideGivenByTelephone?: boolean
  hideDateDay?: boolean
  showConsequences?: boolean
  showGivenByRank?: boolean
  useMilitaryDisciplineActions?: boolean
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  attribute?: string
}

export default class ReprimandItem extends ValidationElement<Props> {
  update = (queue: Partial<ReprimandModel>) => {
    this.props.onUpdate({
      Text: this.props.Text,
      Date: this.props.Date,
      Comments: this.props.Comments,
      GivenBy: this.props.GivenBy,
      GivenByNotApplicable: this.props.GivenByNotApplicable,
      RankUnknown: this.props.RankUnknown,
      GivenByRank: this.props.GivenByRank,
      DisciplinaryAction: this.props.DisciplinaryAction,
      DisciplinaryActionOtherExplaination: this.props.DisciplinaryActionOtherExplaination,
      Consequences: this.props.Consequences,
      ConsequencesOtherExplanation: this.props.ConsequencesOtherExplanation,
      ...queue,
    });
  };

  updateText = (values: ReprimandModel['Text']) => {
    this.update({
      Text: values,
    });
  };

  updateDate = (values: ReprimandModel['Date']) => {
    this.update({
      Date: values,
    });
  };

  updateComments = (values: ReprimandModel['Comments']) => {
    this.update({
      Comments: values,
    });
  };

  updateGivenBy = (values: ReprimandModel['GivenBy']) => {
    this.update({
      GivenBy: values,
    });
  };

  updateGivenByNotApplicable = (values: ReprimandModel['GivenByNotApplicable']) => {
    this.update({
      GivenBy: ((values?.applicable) ? this.props.GivenBy : {} as ReprimandModel['GivenBy']),
      GivenByNotApplicable: values,
    });
  };

  updatePhoneNumbers = (values: ReprimandModel['GivenByTelephone']) => {
    this.update({
      GivenByTelephone: values,
    });
  };

  updateEmails = (values: ReprimandModel['GivenByEmail']) => {
    this.update({
      GivenByEmail: values,
    });
  };

  updateDisciplinaryAction = (values: ReprimandModel['DisciplinaryAction']) => {
    const includesOther = values?.value === 'Other';
    const emptyExplaination = {} as ReprimandModel['DisciplinaryActionOtherExplaination'];
    const DisciplinaryActionOtherExplaination = includesOther ? this.props.DisciplinaryActionOtherExplaination : emptyExplaination;

    this.update({
      DisciplinaryActionOtherExplaination,
      DisciplinaryAction: values,
    });
  };

  updateDisciplinaryActionOtherExplaination = (values: ReprimandModel['DisciplinaryActionOtherExplaination']) => {
    this.update({
      DisciplinaryActionOtherExplaination: values,
    });
  };

  updateConsequences = (values: ReprimandModel['Consequences']) => {
    const list = Checkbox.select(values, this.props.Consequences);
    const includesOther = list.includes('Other');

    this.update({
      Consequences: { values: list },
      ConsequencesOtherExplanation: includesOther ? this.props.ConsequencesOtherExplanation : {}
    });
  };

  updateConsequencesOtherExplanation = (values: ReprimandModel['ConsequencesOtherExplanation']) => {
    this.update({
      ConsequencesOtherExplanation: values,
    });
  };

  updateGivenByRank = (values: ReprimandModel['GivenByRank']) => {
    this.update({
      GivenByRank: values,
    });
  };

  updateRankUnknown = (values: ReprimandModel['RankUnknown']) => {
    this.update({
      GivenByRank: ((values?.applicable) ? this.props.GivenByRank : {} as ReprimandModel['GivenByRank']),
      RankUnknown: values,
    });
  };

  render() {
    const attr = `items[${this.props.$index}].Item`;
    const reprimandDate = getSafeDateTime(this.props.Date);
    const endEmploymentDate = getSafeDateTime(this.props.Dates?.to);

    return (
      <div>
        <Field
          attribute={setAttribute(this.props.attribute, `${attr}.DisciplinaryAction`)}
          title={i18n.t('history.pvq.employment.default.reprimand.type')}
          scrollIntoView={this.props.scrollIntoView}
        >
          {this.props.useMilitaryDisciplineActions ? (
            <RadioGroup
              attribute={setAttribute(this.props.attribute, `${attr}.DisciplinaryAction`)}
              name="DisciplinaryAction"
              className="option-list option-list-vertical"
              onError={this.props.onError}
              required={this.props.required}
              selectedValue={(this.props.DisciplinaryAction || {}).value}
            >
              {militaryDisciplinaryProcedureNamesPvq.map((act) => (
                <Radio
                  key={act}
                  label={act}
                  value={act}
                  onUpdate={this.updateDisciplinaryAction}
                  onError={this.props.onError}
                  onBlur={this.props.onBlur}
                  onFocus={this.props.onFocus}
                />
              ))}
            </RadioGroup>
          ) : (
            <RadioGroup
              attribute={setAttribute(this.props.attribute, `${attr}.DisciplinaryAction`)}
              name="DisciplinaryAction"
              className="option-list option-list-vertical"
              onError={this.props.onError}
              required={this.props.required}
              selectedValue={(this.props.DisciplinaryAction || {}).value}
            >
              {disciplineOptions.map((act) => (
                <Radio
                  key={act}
                  label={act}
                  value={act}
                  onUpdate={this.updateDisciplinaryAction}
                  onError={this.props.onError}
                  onBlur={this.props.onBlur}
                  onFocus={this.props.onFocus}
                />
              ))}
            </RadioGroup>
          )}
        </Field>
        {this.props.DisciplinaryAction?.value === 'Other' && (
          <Field
            attribute={setAttribute(this.props.attribute, `${attr}.DisciplinaryActionOtherExplaination`)}
            title={i18n.t('history.pvq.employment.default.reprimand.otherExplaination')}
            titleSize="h4"
            className="disciplinaryActionOtherExplaination"
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              attribute={setAttribute(this.props.attribute, `${attr}.DisciplinaryActionOtherExplaination`)}
              name="DisciplinaryActionOtherExplaination"
              {...this.props.DisciplinaryActionOtherExplaination}
              onUpdate={this.updateDisciplinaryActionOtherExplaination}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}
        <Field
          attribute={setAttribute(this.props.attribute, `${attr}.Text`)}
          title={i18n.t('history.pvq.employment.default.reprimand.description.label')}
          titleSize="h4"
          className="explanation-left"
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            attribute={setAttribute(this.props.attribute, `${attr}.Text`)}
            name="Text"
            {...this.props.Text}
            onUpdate={this.updateText}
            onError={this.props.onError}
            maxlength="100"
            required={this.props.required}
          />
        </Field>
        { // Warning shown
          reprimandDate?.valid && endEmploymentDate?.valid && reprimandDate.datetime > endEmploymentDate.datetime && (
            <ErrorMessage
              isWarning
              title="Oops, there may be a problem"
              message={[
                'The date for this reprimand incident is after the end of this period of employment.',
                'Please ensure that the date entered is correct and that the incident happened on the date provided.'
              ]}
            />
          )
        }
        <Field
          attribute={setAttribute(this.props.attribute, `${attr}.Date`)}
          title={i18n.t('history.pvq.employment.default.reprimand.date.label')}
          titleSize="h4"
          className="date-left"
          adjustFor="labels"
          shrink
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            attribute={setAttribute(this.props.attribute, `${attr}.Date`)}
            name="Date"
            {...this.props.Date}
            onUpdate={this.updateDate}
            minDateEqualTo
            maxDate={{ year: new Date().getFullYear() }}
            onError={this.props.onError}
            required={this.props.required}
            hideDay={this.props.hideDateDay}
          />
        </Field>
        <Field
          optional
          filterErrors={(errors) => errors.filter((err) => err.code.includes('required'))}
          scrollIntoView={this.props.scrollIntoView}
          title={i18n.t('history.pvq.employment.default.reprimand.name')}
        >
          {this.props.showGivenByRank
            && (
              <Field
                title={i18n.t(`history.pvq.employment.default.reprimand.givenByRank`)}
                titleSize="label"
                className="usa-form-control"
                onError={this.props.onError}
                scrollIntoView={this.props.scrollIntoView}
                adjustFor="labels"
                attribute={setAttribute(this.props.attribute, `${attr}.GivenByRank`)}
              >
                <NotApplicable
                  name="RankUnknown"
                  {...this.props.RankUnknown}
                  label={i18n.t('history.pvq.employment.default.reprimand.rankUnknown')}
                  or={i18n.m('reference.para.or')}
                  onUpdate={this.updateRankUnknown}
                  attribute={setAttribute(this.props.attribute, `${attr}.RankUnknown`)}
                >
                  <Text
                    {...this.props.GivenByRank}
                    name="GivenByRank"
                    className="given-by-rank"
                    onUpdate={this.updateGivenByRank}
                    onError={this.props.onError}
                    required={this.props.required}
                    attribute={setAttribute(this.props.attribute, `${attr}.GivenByRank`)}
                    disabled={this.props.disabled}
                  />
                </NotApplicable>
              </Field>
            )}
          <NotApplicable
            name="nameNotApplicable"
            {...this.props.GivenByNotApplicable}
            label={i18n.t('history.pvq.employment.default.reprimand.nameNotApplicable')}
            or={i18n.m('reference.para.or')}
            onUpdate={this.updateGivenByNotApplicable}
            attribute={setAttribute(this.props.attribute, `${attr}.GivenByNotApplicable`)}
          >
            <PVQName
              attribute={setAttribute(this.props.attribute, `${attr}.GivenBy`)}
              aria-label="Provide full name"
              name="name"
              {...this.props.GivenBy}
              onUpdate={this.updateGivenBy}
              onError={this.props.onError} // eslint-disable-line @typescript-eslint/unbound-method
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              hideMiddleName
              showLastFirstMiddleNameOrder
            />
          </NotApplicable>
        </Field>
        {this.props.hideGivenByTelephone !== true && (
          <TelephoneCollection
            onUpdate={this.updatePhoneNumbers}
            onError={this.props.onError}
            required={this.props.required}
            attribute={setAttribute(this.props.attribute, `${attr}.GivenByTelephone`)}
            telephoneFieldTitle={i18n.t('history.pvq.employment.default.phoneNumbers.title')}
            appendFieldTitle={i18n.t('history.pvq.employment.default.phoneNumbers.append')}
            branchFieldTitle={i18n.t('history.pvq.employment.default.phoneNumbers.branch.title')}
            allowNotApplicable
            hideInitialBranchQuestion
            showTimeOfDay
            timeOfDayLabel={i18n.t('history.pvq.employment.default.phoneNumbers.timeOfDay')}
            {...this.props.GivenByTelephone}
          />
        )}
        {this.props.hideGivenByEmail !== true && (
          <EmailCollection
            onUpdate={this.updateEmails}
            onError={this.props.onError}
            required={this.props.required}
            attribute={setAttribute(this.props.attribute, `${attr}.GivenByEmail`)}
            emailFieldTitle={i18n.t('history.pvq.employment.default.emails.title')}
            appendFieldTitle={i18n.t('history.pvq.employment.default.emails.append')}
            branchFieldTitle={i18n.t('history.pvq.employment.default.emails.branch.title')}
            hasSecondaryAppendLabels
            secondaryNoAppendLabel="No"
            secondaryYesAppendLabel="Yes"
            {...this.props.GivenByEmail}
            showEmailUnknown
            includeEmailType={false}
          />
        )}

        {this.props.showConsequences === true && (
          <Field
            title={i18n.t('history.pvq.employment.heading.consequences')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${this.props.attribute}.Consequences`}
          >
            <CheckboxGroup
              className="ordered-by option-list option-list-vertical"
              required={this.props.required}
              onError={this.props.onError}
              selectedValues={(this.props.Consequences || {}).values}
              attribute={setAttribute(this.props.attribute, `${attr}.Consequences`)}
            >
              {disciplineSentenceOptions.map((option) => (
                <Checkbox
                  label={i18n.t(`history.pvq.disciplinary.heading.label.consequences.${option.i18nField}`)}
                  key={`${option.key}`}
                  name={`employment-outcome-consequences-${option.key}`}
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  onError={this.props.onError}
                  onUpdate={this.updateConsequences}
                  value={`${option.value}`}
                />
              ))}

            </CheckboxGroup>
          </Field>
        )}

        {this.props.showConsequences === true && (this.props.Consequences?.values || []).includes('Other') && (
          <Field
            title={i18n.t('history.pvq.employment.heading.explanation')}
            titleSize="label"
            scrollIntoView={this.props.scrollIntoView}
            attribute={setAttribute(this.props.attribute, `${attr}.ConsequencesOtherExplanation`)}
          >
            <Textarea
              {...this.props.ConsequencesOtherExplanation}
              name="ConsequencesOtherExplanation"
              className="methods-explanation"
              onUpdate={this.updateConsequencesOtherExplanation}
              onError={this.props.onError}
              required={this.props.required}
              attribute={setAttribute(this.props.attribute, `${attr}.ConsequencesOtherExplanation`)}
            />
          </Field>
        )}
      </div>
    );
  }
}

ReprimandItem.defaultProps = {
  Dates: {},
  Comments: {},
  GivenBy: {},
  GivenByNotApplicable: { applicable: true },
  DisciplinaryAction: {},
  DisciplinaryActionOtherExplaination: {},
  GivenByRank: {},
  Consequences: {},
  ConsequencesOtherExplanation: {},
  showConsequences: false,
  showGivenByRank: false,
  useMilitaryDisciplineActions: false,
  hideGivenByEmail: false,
  hideGivenByTelephone: false,
  hideDateDay: false,
  isPVQ: false,
  RankUnknown: {},
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
};
